import { useListContext } from 'react-admin'

interface HighlightedTextProps {
  text: string
}

/**
 * HighlightedText - Highlights search terms in text
 *
 * Highlights all search terms (from filter.q) that appear in the text.
 * Uses case-insensitive matching and wraps matches in <mark> tags.
 */
const HighlightedText = ({ text }: HighlightedTextProps) => {
  const { filterValues } = useListContext()
  const searchQuery = filterValues?.q as string | undefined

  if (!text) return null
  if (!searchQuery || !searchQuery.trim()) {
    return <>{text}</>
  }

  // Parse search query into terms (handle quoted phrases and individual words)
  const terms: string[] = []
  const phraseRegex = /"([^"]+)"/g
  let match
  let remaining = searchQuery

  // Extract quoted phrases first
  while ((match = phraseRegex.exec(searchQuery)) !== null) {
    terms.push(match[1])
    remaining = remaining.replace(match[0], '')
  }

  // Add remaining individual words
  remaining.split(/\s+/).forEach((word) => {
    if (word.trim()) {
      terms.push(word.trim())
    }
  })

  if (terms.length === 0) {
    return <>{text}</>
  }

  // Create a regex that matches any of the terms (case-insensitive)
  const escapedTerms = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi')

  // Split text by matches and wrap matches in <mark>
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = terms.some((term) => part.toLowerCase() === term.toLowerCase())
        return isMatch ? (
          <mark
            key={index}
            style={{
              backgroundColor: '#fff59d',
              padding: '0 2px',
              borderRadius: '2px',
              fontWeight: 500,
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      })}
    </>
  )
}

export default HighlightedText
